// Marker geometry + sector logic.
//
// A marker is `{ segmentIdx, t }` pointing at a position on the track
// centerline. The schema has four marker slots: startFinish, startLine,
// and two sector boundaries.
//
// All functions here are pure — they operate on data passed in, no
// closures over track / state. The unified markerLineEndpoints respects
// `direction` (cw / ccw); the editor's standalone HTML version did not,
// which was a real geometry-agreement bug per CLAUDE.md.

import { curvePointAt, curvePointAtOpen, curveHeadingAt, widthAt } from './spline.js';

// Sector tint + line palette. Cool colors get more saturation so they
// read against the gray track surface (cyan needs more than amber).
export const SECTOR_COLORS = [
  { tint: 'rgba(77, 208, 225, 0.38)',  line: '#4dd0e1' },   // sector 1: cyan
  { tint: 'rgba(255, 183, 77, 0.32)',  line: '#ffb74d' },   // sector 2: amber
  { tint: 'rgba(255, 90, 90, 0.36)',   line: '#ff5a5a' },   // sector 3: red
];

// Perpendicular unit vector to the track centerline tangent at
// (segIdx, t). "Right of direction of travel" if the spline is wound CW,
// flipped if CCW — but the perp itself is direction-agnostic since the
// rotation is fixed.
export function trackPerpendicularAt(waypoints, segIdx, t, alpha = 0.5) {
  if (waypoints.length < 3) return { x: 0, y: 1 };
  const eps = 0.005;
  const t1 = Math.max(0, t - eps);
  const t2 = Math.min(1 - 1e-6, t + eps);
  const p1 = curvePointAt(waypoints, segIdx, t1, alpha);
  const p2 = curvePointAt(waypoints, segIdx, t2, alpha);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return { x: 0, y: 1 };
  // Rotate tangent +90° → perpendicular.
  return { x: -dy / len, y: dx / len };
}

// Visible (track-width) line endpoints + tangent + perp for a marker.
// Returns null if the track doesn't have enough geometry yet.
//
// `direction` is 'cw' or 'ccw'. The tangent is flipped 180° on CCW
// tracks because waypoints are stored in authored order regardless of
// direction — so the natural curve tangent always points along authored
// order, which on CCW means BACKWARD around the lap. Flipping keeps
// timing-line crossings (forward-direction dot product) correct.
//
// Perp stays untouched: only the tangent's sign matters for crossings,
// and visually it doesn't matter which side of the line is "left."
// `spline` carries `{ waypoints, alpha?, width? }` — works for the
// closed trackline and for the open pit lane (different `closed`).
// `direction` only matters for closed loops where the schema's CCW
// flag flips the natural tangent. Open splines are uni-directional in
// authored order, so `direction` is ignored when `closed === false`.
export function markerLineEndpoints(marker, spline, direction = 'cw', closed = true) {
  if (!marker) return null;
  const wps = spline.waypoints;
  if (closed && wps.length < 3) return null;
  if (!closed && wps.length < 2) return null;
  const alpha = spline.alpha ?? 0.5;
  const defaultWidth = spline.width ?? 16;

  const evalCurve = closed ? curvePointAt : curvePointAtOpen;
  const center  = evalCurve(wps, marker.segmentIdx, marker.t, alpha);
  const heading = curveHeadingAt(wps, marker.segmentIdx, marker.t, alpha, closed);
  const fwd = (closed && direction === 'ccw') ? -1 : 1;
  const tangent = { x: Math.cos(heading) * fwd, y: Math.sin(heading) * fwd };
  const perp    = { x: -Math.sin(heading), y: Math.cos(heading) };
  // Local width at the marker's position so the visible portion of
  // any timing line matches a variable-width track (or pit lane).
  const half = widthAt(wps, defaultWidth, marker.segmentIdx, marker.t, closed) / 2;
  return {
    center,
    tangent,
    perp,
    a: { x: center.x - perp.x * half, y: center.y - perp.y * half },
    b: { x: center.x + perp.x * half, y: center.y + perp.y * half },
  };
}

// Lap progress (0..1) of a position relative to S/F, walking forward
// around the closed loop. Uses (segIdx + t) as approximate arc
// parameterization — not perfectly arc-length-uniform, but monotonic
// and adequate for sector logic.
export function progressFromSF(segIdx, t, startFinish, numWaypoints) {
  if (!startFinish || numWaypoints < 3) return 0;
  const sfPos = startFinish.segmentIdx + startFinish.t;
  const myPos = segIdx + t;
  let progress = (myPos - sfPos) / numWaypoints;
  while (progress < 0) progress += 1;
  while (progress >= 1) progress -= 1;
  return progress;
}

// Which sector (0, 1, or 2) is this point in? Requires both sector
// boundaries set.
export function sectorOfPoint(segIdx, t, sectors, startFinish, numWaypoints) {
  if (!Array.isArray(sectors) || sectors.length !== 2) return 0;
  const myProg = progressFromSF(segIdx, t, startFinish, numWaypoints);
  const p0 = progressFromSF(sectors[0].segmentIdx, sectors[0].t, startFinish, numWaypoints);
  const p1 = progressFromSF(sectors[1].segmentIdx, sectors[1].t, startFinish, numWaypoints);
  const [pFirst, pSecond] = p0 < p1 ? [p0, p1] : [p1, p0];
  if (myProg < pFirst) return 0;
  if (myProg < pSecond) return 1;
  return 2;
}

// Keep sectors[0] always at the lower-progress boundary so labels stay
// stable. Mutates the array in place; returns true if a swap happened
// (caller can re-bind any active-marker reference).
export function normalizeSectors(sectors, startFinish, numWaypoints) {
  if (!Array.isArray(sectors) || sectors.length !== 2) return false;
  const p0 = progressFromSF(sectors[0].segmentIdx, sectors[0].t, startFinish, numWaypoints);
  const p1 = progressFromSF(sectors[1].segmentIdx, sectors[1].t, startFinish, numWaypoints);
  if (p0 > p1) {
    [sectors[0], sectors[1]] = [sectors[1], sectors[0]];
    return true;
  }
  return false;
}
